import { Edit } from '@mui/icons-material'
import { Button, Card, CardContent, CircularProgress, Grid, Typography, Divider } from '@mui/material'
import { ExplanationAccordion, LabelField } from 'components'
import { useEffect, useState } from 'react'
import ExportInformationAddEditDialog from './ExportInformationAddEditDialog'
import { CompanyDto, CompanyExporterInformationDto } from 'dtos'
import * as controllers from 'controllers'
import { enqueueSnackbar } from 'notistack'
import { useAuth } from 'context'

interface ExportInformationListCardProps {
  company: CompanyDto
}

export default function ExportInformationListCard({ company }: ExportInformationListCardProps) {
  const { permissions } = useAuth()

  const [companyExporterInformation, setCompanyExporterInformation] = useState<CompanyExporterInformationDto>(
    new CompanyExporterInformationDto()
  )
  const [exportInformationAddEditDialogOpen, setExportInformationAddEditDialogOpen] = useState<boolean>(false)
  const [isGettingCompanyExporterInformation, setIsGettingCompanyExporterInformation] = useState<boolean>(true)
  const [isSavingCompanyExporterInformation, setIsSavingCompanyExporterInformation] = useState<boolean>(false)

  useEffect(() => {
    if (company.companyGuid) {
      setIsGettingCompanyExporterInformation(true)
      controllers
        .getCompanyExporterInformation(company.companyGuid)
        .then(response => {
          setCompanyExporterInformation(response.value)
        })
        .finally(() => {
          setIsGettingCompanyExporterInformation(false)
        })
    }
  }, [company])

  return (
    <>
      {permissions.COMPANIES_AND_PEOPLE_ADD_EDIT && (
        <ExportInformationAddEditDialog
          initialValues={companyExporterInformation}
          loading={isSavingCompanyExporterInformation}
          onClose={() => {
            setExportInformationAddEditDialogOpen(false)
          }}
          onSave={values => {
            setIsSavingCompanyExporterInformation(true)
            controllers
              .updateCompanyExporterInformation(company.companyGuid!, values)
              .then(response => {
                enqueueSnackbar('Exporter Information Saved Successfully!', { variant: 'success' })
                setCompanyExporterInformation(response.value)
                setExportInformationAddEditDialogOpen(false)
              })
              .finally(() => {
                setIsSavingCompanyExporterInformation(false)
              })
          }}
          open={exportInformationAddEditDialogOpen}
        />
      )}

      <Card>
        <CardContent>
          <Grid container spacing={1} alignItems='flex-start'>
            <Grid item xs={12} container alignItems='center' justifyContent='space-between'>
              <Grid item xs={12} sm='auto'>
                <Typography fontWeight='bold' variant='h5'>
                  Exporter Information
                </Typography>
              </Grid>

              <Grid item xs={12} sm='auto'>
                {permissions.COMPANIES_AND_PEOPLE_ADD_EDIT && (
                  <Button
                    endIcon={<Edit />}
                    onClick={() => {
                      setExportInformationAddEditDialogOpen(true)
                    }}
                    variant='text'
                  >
                    EDIT
                  </Button>
                )}
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            {isGettingCompanyExporterInformation && (
              <Grid item container alignItems='center' justifyContent='center' sx={{ mt: 1 }}>
                <Grid item>
                  <CircularProgress color='primary' size={50} />
                </Grid>
              </Grid>
            )}

            {!isGettingCompanyExporterInformation && (
              <>
                <Grid item xs={12}>
                  <LabelField label='Dealer #' value={companyExporterInformation.exporterDealerNumber} />
                </Grid>

                <Grid item xs={12}>
                  <LabelField label='Description' value={companyExporterInformation.exporterDescription} />
                </Grid>

                <Grid item xs={12}>
                  <LabelField label='IRS #' value={companyExporterInformation.exporterIrsNumber} />
                </Grid>

                <Grid item xs={12}>
                  <LabelField label='Exporter Code' value={companyExporterInformation.exporterCode} />
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <ExplanationAccordion variant='outlined'>
                ||This card only shows if the company is marked as an Exporter Company. ||
              </ExplanationAccordion>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </>
  )
}
